import type { FC } from 'react'
import { useRef, useState } from 'react'
import { IconButton } from '@mui/material'
import { alpha } from '@mui/material/styles'
import AssistantRoundedIcon from '@mui/icons-material/AssistantRounded'
import { useFocusOnOpen } from '~/shared/hooks/useFocusOnOpen'
import { ArrowTooltip } from '~/shared/ui'
import { LuciPanel } from './LuciPanel'
import { useLuci } from './useLuci'

interface LuciProps {
  personId: number
  patientId: number | null
}

/** The "Ask Luci" launcher for the member header's action column and the panel it opens over the
 * member profile's left column, for a staff member whose flag is on. */
export const Luci: FC<LuciProps> = ({ personId, patientId }) => {
  const enabled = useLuci()
  const [open, setOpen] = useState(false)
  const [everOpened, setEverOpened] = useState(false)
  const [closedOnce, setClosedOnce] = useState(false)
  const launcherRef = useRef<HTMLButtonElement>(null)
  // Focus returns to the launcher after a close, never on first render.
  useFocusOnOpen(launcherRef, closedOnce && !open)

  const openPanel = () => {
    setOpen(true)
    setEverOpened(true)
  }
  const close = () => {
    setOpen(false)
    setClosedOnce(true)
  }

  if (!enabled) return null
  return (
    <>
      {!open && (
        <ArrowTooltip title="Ask Luci">
          <IconButton
            ref={launcherRef}
            size="small"
            aria-label="Ask Luci"
            onClick={openPanel}
            data-testid="luci-launcher"
            sx={theme => ({
              flex: '0 0 auto',
              color: 'primary.main',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            })}
          >
            <AssistantRoundedIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
        </ArrowTooltip>
      )}
      {everOpened && (
        <LuciPanel personId={personId} patientId={patientId} open={open} onClose={close} />
      )}
    </>
  )
}
